<template>
  <div
    v-if="$featureFlags.isEnabled('ilr-management-page') && $permissions.hasPermission('access_ilr_management')"
    class="content-with-spacing manage-ilr-summary"
    :class="sidebarClass"
  >
    <h1>Manage ILR Summary</h1>
    <section v-if="learnerDataReady && learnerInfo" class="learner-info-banner">
      <k-avatar :avatarUrl="learnerInfo.avatar_url"></k-avatar>
      <div class="learner-info-text" >
        <h2 id="learner-name" class="learner-name">{{ learnerName }}</h2>
        <span>Learner ID: <span class="learner-id">{{ formattedId }}</span></span>
        <span>Email: <b>{{ learnerInfo.email }}</b></span>
      </div>
    </section>
    <section class="pics-panel" v-if="ready">
      <k-panel :hasContentToggle="true" :showContent="showPicsEpisodes" @toggle="showPicsEpisodes = $event" title="PICS Episodes" icon="fa-list-ul" :duotone="false">
        <template #body>
          <learner-pics-episodes
            :learnerInfo="learnerInfo"
            :picsEpisodes="picsEpisodes"
            :unlinkedEpisodes="unlinkedEpisodes"
            @reassign-episode="openReassignEpisodeModal"
            @search-pics="searchPicsModalOpen = true"
            @assign-episode="openAssignEpisodeModal"
            @unassign-episode="unlinkEpisode"
          />
        </template>
      </k-panel>
    </section>
    <section class="programmes-panel" v-if="learnerDataReady && linkedProgrammeEpisodesReady">
      <k-panel :hasContentToggle="true" :showContent="showProgrammes" @toggle="showProgrammes = $event" title="Programmes" icon="fa-diagram-project">
        <template #body>
          <ilr-programme-card
            v-for="programme in linkedProgrammeEpisodes"
            :learner-info="learnerInfo"
            :programme="programme"
            :key="programme.id"
          />
        </template>
      </k-panel>
    </section>
    <search-pics-modal
      v-if="searchPicsModalOpen"
      :showModal="searchPicsModalOpen"
      :learnerId="learnerId"
      :existingUnlinkedEpisodes="unlinkedEpisodes"
      @close="searchPicsModalOpen = false"
      @episodes-selected="addEpisodesToUnlinked"
    />
    <assign-reassign-episode-modal
      v-if="selectedEpisode"
      :showModal="assignModalOpen"
      :episode="selectedEpisode"
      :programmes="linkedProgrammeEpisodes"
      :learnerId="learnerId"
      :mode="assignMode"
      @close="assignModalOpen = false"
      @episode-assigned="updateEpisodeData"
    />
  </div>
</template>

<style>
.manage-ilr-summary h2 > i.k-asset-icon {
  font-size: 1em;
}
</style>

<style scoped>
.learner-info-banner {
  display: flex;
  gap: 10px;
  align-items: center;
  margin-bottom: 35px;
}

.learner-info-text {
  display: flex;
  flex-direction: column;
}

.learner-name {
  margin: 0;
}

.learner-id {
  color: var(--kate-type-secondary);
}
</style>

<script>
import KAvatar from '@base-components/k-avatar.vue';
import SidebarMixin from '@mixins/sidebar-mixin';
import PageReadyMixin from '@mixins/page-ready-mixin';
import { formatId } from '@utils/format-user-id';
import KPanel from '@base-components/k-panel.vue';
import IlrProgrammeCard from './ilr-programme-card.vue';
import LearnerPicsEpisodes from './learner-pics-episodes.vue';
import SearchPicsModal from './search-pics-modal.vue';
import AssignReassignEpisodeModal from './assign-episode-modal.vue';

export default {
  name: 'ManageIlrSummary',
  components: {
    KAvatar,
    KPanel,
    IlrProgrammeCard,
    LearnerPicsEpisodes,
    SearchPicsModal,
    AssignReassignEpisodeModal,
  },

  mixins: [SidebarMixin, PageReadyMixin],

  data() {
    return {
      learnerName: undefined,
      learnerInfo: undefined,
      learnerDataReady: false,
      showPicsEpisodes: true,
      showProgrammes: true,
      picsEpisodesReady: false,
      picsEpisodes: [],
      linkedProgrammeEpisodesReady: false,
      linkedProgrammeEpisodes: [],
      unlinkedEpisodes: [],
      selectedEpisode: undefined,
      searchPicsModalOpen: false,
      assignModalOpen: false,
    };
  },

  computed: {
    ready() {
      return this.learnerDataReady && this.linkedProgrammeEpisodesReady && this.picsEpisodesReady;
    },
    learnerId() {
      return Number(this.$route.params.studentId);
    },
    formattedId() {
      return formatId(this.learnerId);
    },
  },

  beforeMount() {
    this.$Loading.start();
    this.getLearnerData();
    this.getAllLearnerPicsEpisodes();
    this.getLinkedProgrammeEpisodes();
  },

  methods: {
    registerCrumbs() {
      if (!this.learnerName) {
        return;
      }
      this.$crumbs.register([
        {
          text: this.learnerName,
          path: {
            name: 'dashboard_students',
            params: { studentId: this.learnerId },
          },
        },
        { text: 'Manage ILR Summary', active: true },
      ]);
    },
    getLearnerData() {
      this.$logger.info('Getting learner profile', { studentId: this.learnerId }, true);
      this.learnerDataReady = false;
      this.$http.get(`/api/profile/users/${this.learnerId}`)
        .then(result => {
          this.learnerName = result.data.full_name;
          this.learnerInfo = result.data;
          this.registerCrumbs();
          this.$logger.info('Got learner profile', { studentId: this.learnerId });
        }).catch(error => {
          if (this.$http.errIn(error, [403])) {
            this.$logger.warn('User does not have permission to view this learner', { studentId: this.learnerId }, error);
            this.$router.push({ name: '403' });
            return;
          }
          this.$logger.error('Error getting learner profile', { studentId: this.learnerId }, error);
          this.$ktoast.showError(error);
        }).then(() => {
          this.learnerDataReady = true;
        });
    },

    getAllLearnerPicsEpisodes() {
      this.$logger.info('Getting all learner PICS episodes', { studentId: this.learnerId }, true);
      this.picsEpisodesReady = false;
      return this.$http.get(`/api/profile/ilr/user/${this.learnerId}/episodes`)
        .then(result => {
          this.picsEpisodes = result.data.episodes;
          this.$logger.info('Got all learner PICS episodes', { studentId: this.learnerId });
        }).catch(error => {
          this.$logger.error('Error getting all learner PICS episodes', { studentId: this.learnerId }, error);
          this.$ktoast.showError(error);
          throw error;
        }).finally(() => {
          this.picsEpisodesReady = true;
        });
    },

    getLinkedProgrammeEpisodes() {
      this.$logger.info('Getting all linked programme PICS episodes', { studentId: this.learnerId }, true);
      this.linkedProgrammeEpisodesReady = false;
      return this.$http.get(`/api/profile/ilr/user/${this.learnerId}/programmes`)
        .then(result => {
          this.linkedProgrammeEpisodes = result.data.programmes;
          this.$logger.info('Got all linked programme PICS episodes', { studentId: this.learnerId });
        }).catch(error => {
          this.$logger.error('Error getting all linked programme PICS episodes', { studentId: this.learnerId }, error);
          this.$ktoast.showError(error);
          throw error;
        }).finally(() => {
          this.linkedProgrammeEpisodesReady = true;
        });
    },

    unlinkEpisode(episode) {
      this.$ktoast.confirmToast('Confirm that you want to unlink this episode.', 'warning', () => {
        this.$logger.info('Unlinking episode', { episodeId: episode.pics_id });
        this.$http.delete(`/api/profile/ilr/user/${this.learnerId}/episodes/${episode.pics_id}/link`)
          .then(() => {
            this.$logger.info('Episode unlinked', { episodeId: episode.pics_id });
            this.updateEpisodeData().then(() => {
              this.$ktoast.success('Episode unlinked successfully');
            });
          })
          .catch(err => {
            this.$logger.error('Error unlinking episode', undefined, err);
            this.$ktoast.showError(err);
          });
      });
    },

    openReassignEpisodeModal(episode) {
      this.assignModalOpen = true;
      this.assignMode = 'reassign';
      this.selectedEpisode = episode;
    },

    openAssignEpisodeModal(episode) {
      this.assignModalOpen = true;
      this.assignMode = 'assign';
      this.selectedEpisode = episode;
    },

    tidyUnlinkedEpisodes() {
      const linkedIds = this.picsEpisodes.map(episode => episode.id);
      this.unlinkedEpisodes = this.unlinkedEpisodes.filter(episode => !linkedIds.includes(episode.pics_id));
    },

    async updateEpisodeData() {
      await this.getAllLearnerPicsEpisodes();
      await this.getLinkedProgrammeEpisodes();
      this.tidyUnlinkedEpisodes();
    },

    addEpisodesToUnlinked(episodes) {
      this.unlinkedEpisodes = [...this.unlinkedEpisodes, ...episodes];
    },
  },
};
</script>
